// The live feed from design/project/App09l-LobbyLive and App09f-LiveFeed: a line in the lobby that rotates through the
// latest win and today's numbers, and the LIVE sheet it opens. Shown only while the admin switch is on.
import { useEffect, useState } from "react";
import { api } from "../api.js";

interface FeedWin {
  battleId: string;
  name: string;
  arena: string;
  side: "long" | "short";
  durationSec: number;
  amount: string;
  big: boolean;
  at: number;
}
interface Feed {
  on: boolean;
  today?: number;
  battlingNow?: number;
  best?: { name: string; amount: string } | null;
  wins?: FeedWin[];
}

const UN = { fontFamily: "Unbounded, sans-serif", fontWeight: "900" } as const;
const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;
const COLOURS = ["#8A7CFF", "#2AABEE", "#FF8A3D", "#FF3366", "#3DFFD0", "#9945FF", "#F7931A"];
const colourOf = (name: string) => COLOURS[[...name].reduce((s, c) => s + c.charCodeAt(0), 0) % COLOURS.length];

/** The feed, refreshed every 30 seconds and the moment a new win arrives on the live socket. */
function useFeed(): Feed | null {
  const [feed, setFeed] = useState<Feed | null>(null);
  useEffect(() => {
    let stop = false;
    const load = () => void api<Feed>("/v1/feed").then((f) => !stop && setFeed(f)).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    const onWin = (e: Event) => {
      const win = (e as CustomEvent<{ win?: FeedWin }>).detail?.win;
      if (!win) return;
      setFeed((f) => (f?.on ? { ...f, today: (f.today ?? 0) + 1, wins: [win, ...(f.wins ?? []).filter((w) => w.battleId !== win.battleId)].slice(0, 20) } : f));
    };
    addEventListener("dd:feed", onWin);
    return () => {
      stop = true;
      clearInterval(t);
      removeEventListener("dd:feed", onWin);
    };
  }, []);
  return feed;
}

function ago(at: number): string {
  const s = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
}

/** The lobby's live line: the latest win, today's battles, who is battling now, and the unlock hint, in turn. */
export function LiveLine({ hint }: { hint: string | null }) {
  const feed = useFeed();
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setI((n) => n + 1), 4_000);
    return () => clearInterval(t);
  }, []);
  if (!feed?.on) return hint ? <span style={{ fontSize: "12px", color: "#8B949E" }}>{hint}</span> : null;
  const lines: JSX.Element[] = [];
  const w = feed.wins?.[0];
  if (w) lines.push(<><b style={{ color: "#CCFF00" }}>{w.name}</b> won <b style={MONO}>+{w.amount}</b> on {w.arena}</>);
  if (feed.today) lines.push(<><b style={MONO}>{feed.today.toLocaleString("en-US")}</b> battles today</>);
  if (feed.battlingNow) lines.push(<><b style={MONO}>{feed.battlingNow}</b> battling right now</>);
  if (hint) lines.push(<>{hint}</>);
  if (!lines.length) lines.push(<>Live</>);
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Open the live feed" style={{ display: "flex", alignItems: "center", gap: "6px", maxWidth: "68%", minWidth: "0", height: "24px", padding: "0 10px", borderRadius: "999px", border: "1px solid rgba(204,255,0,0.35)", background: "rgba(204,255,0,0.08)", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontSize: "12px", cursor: "pointer" }}>
        <span style={{ flex: "none", width: "7px", height: "7px", borderRadius: "50%", background: "#CCFF00", boxShadow: "0 0 0 3px rgba(204,255,0,0.2)" }}></span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lines[i % lines.length]}</span>
      </button>
      {open && <LiveSheet feed={feed} close={() => setOpen(false)} />}
    </>
  );
}

function Stat({ label, value, sub, lime }: { label: string; value: string; sub: string; lime?: boolean }) {
  return (
    <div style={{ minWidth: "0", padding: "10px 6px", borderRadius: "14px", background: "#0B0E14", border: "2px solid #30363D", display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
      <span style={{ ...UN, fontSize: "8px", color: "#8B949E", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ ...MONO, fontSize: "16px", color: lime ? "#CCFF00" : "#FFFFFF" }}>{value}</span>
      <span style={{ fontSize: "10px", color: "#8B949E", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
    </div>
  );
}

function LiveSheet({ feed, close }: { feed: Feed; close: () => void }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div role="dialog" aria-modal="true" onClick={close} style={{ position: "fixed", inset: "0", zIndex: 40, background: "rgba(11,14,20,0.74)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ boxSizing: "border-box", width: "100%", maxWidth: "480px", alignSelf: "center", maxHeight: "calc(100% - var(--tg-safe-area-inset-top, 47px) - var(--tg-content-safe-area-inset-top, 47px))", padding: "16px 16px calc(var(--tg-safe-area-inset-bottom, 34px) + var(--tg-content-safe-area-inset-bottom, 0px) + 12px)", borderRadius: "26px 26px 0 0", background: "#161B22", borderTop: "3px solid #CCFF00", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
        <div style={{ alignSelf: "center", width: "40px", height: "4px", borderRadius: "2px", background: "#30363D", marginBottom: "12px" }}></div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#FF3366", boxShadow: "0 0 0 4px rgba(255,51,102,0.2)" }}></span>
          <span style={{ ...UN, fontSize: "14px", letterSpacing: "0.06em" }}>LIVE</span>
          <button onClick={close} aria-label="Close" style={{ marginLeft: "auto", width: "36px", height: "36px", border: "0", background: "none", color: "#8B949E", fontSize: "22px", cursor: "pointer" }}>×</button>
        </div>
        <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
          <Stat label="TODAY" value={(feed.today ?? 0).toLocaleString("en-US")} sub="battles" />
          <Stat label="RIGHT NOW" value={String(feed.battlingNow ?? 0)} sub="battling" lime />
          <Stat label="BEST TODAY" value={feed.best ? `+${feed.best.amount.replace(/\.\d+$/, "")}` : "–"} sub={feed.best?.name ?? "no wins yet"} lime />
        </div>
        <span style={{ marginTop: "14px", fontSize: "11px", fontWeight: "600", letterSpacing: "0.1em", color: "#8B949E" }}>LATEST WINS</span>
        <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "2px", overflowY: "auto", minHeight: "0" }}>
          {!feed.wins?.length && <span style={{ padding: "16px 0", textAlign: "center", fontSize: "13px", color: "#8B949E" }}>No wins yet today. Be the first.</span>}
          {feed.wins?.map((w) => (
            <div key={w.battleId} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 10px", borderRadius: "14px", border: w.big ? "1px solid rgba(204,255,0,0.5)" : "1px solid transparent", background: w.big ? "rgba(204,255,0,0.08)" : "transparent" }}>
              <div className="av" style={{ flex: "none", width: "32px", height: "32px", background: colourOf(w.name), fontSize: "10px" }}>{w.name.replace(/^@/, "").slice(0, 2).toUpperCase()}</div>
              <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "6px", minWidth: "0" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><b>{w.name}</b> won on {w.arena} · {w.durationSec === 60 ? "Blitz" : w.side.toUpperCase()}</span>
                  {w.big && <span style={{ flex: "none", height: "16px", padding: "0 6px", borderRadius: "6px", background: "#FF3366", color: "#0B0E14", ...UN, fontSize: "8px", display: "inline-flex", alignItems: "center" }}>BIG WIN</span>}
                </span>
                <span style={{ fontSize: "11px", color: "#8B949E" }}>{ago(w.at)}</span>
              </div>
              <span style={{ ...MONO, fontSize: "13px", color: "#CCFF00", flex: "none" }}>+{w.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
